class ProductVariants extends HTMLElement {
  connectedCallback() {
    this._variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
    this._titleElement = this.querySelector('[data-variant-title]');
    this._form = this.querySelector('form');
    this._variantInput = this.querySelector('[data-variant-input]');
    this._addButton = this.querySelector('[data-add-to-cart]');
    this._cartStatus = this.querySelector('[data-cart-status]');
    this._bundleOptions = this.querySelector('[data-bundle-options]');
    this._selectedBundleId = 'product-only';
    this._selectorButtons = Array.from(this.querySelectorAll('[data-variant-id]'));
    this._carouselElement = this.querySelector('[data-carousel]');
    this._carouselTrack = this.querySelector('[data-carousel-track]');
    this._carouselDots = this.querySelector('[data-carousel-dots]');
    this._carouselPrevious = this.querySelector('[data-carousel-prev]');
    this._carouselNext = this.querySelector('[data-carousel-next]');
    this._carouselIndex = 0;

    this._selectorButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.selectVariant(button.dataset.variantId, true);
      });
    });

    this._carouselPrevious.addEventListener('click', () => {
      this.showCarouselImage(this._carouselIndex - 1);
    });

    this._carouselNext.addEventListener('click', () => {
      this.showCarouselImage(this._carouselIndex + 1);
    });

    this._form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.addSelectedVariantToCart();
    });

    this.selectVariant(this.getInitialVariantId(), false);
    this.preloadVariantImages();
  }

  getInitialVariantId() {
    const variantId = new URLSearchParams(window.location.search).get('variant');

    if (this._variants.some((variant) => String(variant.id) === variantId)) {
      return variantId;
    }

    return this.dataset.initialVariantId;
  }

  selectVariant(variantId, updateUrl) {
    const variant = this._variants.find((item) => String(item.id) === String(variantId));

    if (!variant) {
      return;
    }

    const previousVariantId = this._variantInput.value;
    this._titleElement.textContent = variant.title;
    this._variantInput.value = variant.id;
    this.renderCarousel(variant.mediaImages);
    this.renderBundles(variant);
    this._addButton.disabled = this._addingToCart || !variant.available;
    this._addButton.textContent = variant.available
      ? this.dataset.addToCartLabel
      : this.dataset.unavailableLabel;
    this._cartStatus.textContent = '';

    this._selectorButtons.forEach((button) => {
      const isSelected = button.dataset.variantId === String(variant.id);
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (String(previousVariantId) !== String(variant.id)) {
        window.MarmeladeAnalytics.capture('design selected', {
          surface: 'pdp',
          product_id: this.dataset.productId,
          product_handle: this.dataset.productHandle,
          device_handle: this.dataset.deviceHandle,
          variant_id: String(variant.id),
          design_title: variant.designTitle || variant.title
        });
      }
    }
  }

  async addSelectedVariantToCart() {
    if (this._addingToCart) return;
    const selectedVariant = this._variants.find((item) => String(item.id) === String(this._variantInput.value));
    if (!selectedVariant?.available) return;
    const selectedBundleId = this._selectedBundleId;
    const items = this.getSelectedCartItems();

    if (!items.length) {
      return;
    }

    this._addingToCart = true;
    this._addButton.disabled = true;
    this._addButton.textContent = this.dataset.addingToCartLabel;
    this._cartStatus.textContent = '';

    try {
      const response = window.MarmeladeIphoneCompleteCart
        ? null : await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items,
          sections: 'cart-overlay',
          sections_url: window.Shopify.routes.root,
        }),
      });

      const result = response ? await response.json()
        : await window.MarmeladeIphoneCompleteCart.add(items, { variant: selectedVariant });

      if (response && !response.ok) {
        throw new Error(result.description || this.dataset.addToCartErrorLabel);
      }

      try {
        window.MarmeladeAnalytics.capture('product added to cart', {
          surface: 'pdp',
          product_id: this.dataset.productId,
          product_handle: this.dataset.productHandle,
          device_handle: this.dataset.deviceHandle,
          variant_id: String(selectedVariant?.id || ''),
          design_title: selectedVariant?.designTitle || selectedVariant?.title || '',
          bundle_id: selectedBundleId,
          item_count: items.length,
          quantity: items.reduce((total, item) => total + Number(item.quantity || 0), 0)
        });
      } catch (error) {
        console.error('[Marmelade analytics] Cart addition was accepted, but tracking failed.', error);
      }

      this._cartStatus.textContent = this.dataset.addedToCartLabel;
      const variantImages = {};
      if (selectedVariant?.mediaImages?.[0]?.url) {
        variantImages[String(selectedVariant.id)] = selectedVariant.mediaImages[0].url;
      }
      selectedVariant?.bundles?.forEach((bundle) => {
        bundle.contents?.forEach((item) => {
          if (item.variantId && item.image?.url) {
            variantImages[String(item.variantId)] = item.image.url;
          }
        });
      });
      this.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cartAdd: result,
            variantImages: variantImages,
          },
        })
      );
    } catch (error) {
      this._cartStatus.textContent = error.message || this.dataset.addToCartErrorLabel;
    } finally {
      this._addingToCart = false;
      const variant = this._variants.find((item) => String(item.id) === String(this._variantInput.value));
      this._addButton.disabled = !variant || !variant.available;
      this._addButton.textContent =
        variant && variant.available
          ? this.dataset.addToCartLabel
          : this.dataset.unavailableLabel;
    }
  }

  getSelectedCartItems() {
    const variant = this._variants.find((item) => String(item.id) === String(this._variantInput.value));

    if (!variant) {
      return [];
    }

    // Keep the default template's explicit product-only choice, while sharing
    // bundle grouping, discount metadata and display-only accessory filtering.
    const items = variant.bundles.flatMap((bundle) => bundle.contents);
    return window.MarmeladeProductPurchase.getSelectedCartItems.call({
      dataset: this.dataset,
      _selectedBundleId: this._selectedBundleId,
      _selectedAddonIds: new Set(),
      getEssentialBundle: () => null,
      getEssentialItems: () => [],
      getBundleItems: () => items,
      getOptionalItems: () => items,
      getAddonId: (item) => String(item.variantId),
      shouldAddBundleItemToCart: (item) => {
        const handle = String(item.productHandle || '').toLowerCase();
        const title = String(item.productTitle || '').toLowerCase().replaceAll('ö', 'o').replaceAll('ü', 'u');
        return !['zubehor', 'zubehoer'].some((name) => title === name || handle === name || handle.startsWith(`${name}-`));
      },
    }, variant);
  }

  renderBundles(variant) {
    const choices = [
      {
        id: 'product-only',
        title: this.dataset.productOnlyLabel,
        image: null,
        contents: [],
        originalTotalFormatted: '',
        discountedTotalFormatted: '',
      },
      ...variant.bundles,
    ];

    if (!choices.some((choice) => choice.id === this._selectedBundleId)) {
      this._selectedBundleId = 'product-only';
    }

    this._bundleOptions.replaceChildren();

    choices.forEach((choice) => {
      const label = document.createElement('label');
      label.className = 'product__bundle-option';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'bundle';
      input.value = choice.id;
      input.checked = choice.id === this._selectedBundleId;
      input.addEventListener('change', () => {
        this._selectedBundleId = choice.id;
      });
      label.append(input);

      if (choice.image) {
        const image = document.createElement('img');
        image.src = choice.image.url;
        image.alt = choice.image.alt || '';
        image.loading = 'lazy';
        label.append(image);
      }

      const title = document.createElement('span');
      title.textContent = choice.title;
      label.append(title);

      if (choice.contents.length) {
        const contents = document.createElement('small');
        contents.textContent = `${this.dataset.bundleContentsLabel}: ${choice.contents
          .map((item) => item.productTitle)
          .join(', ')}`;
        label.append(contents);
      }

      if (choice.discountedTotalFormatted) {
        const total = document.createElement('small');
        total.textContent =
          choice.discountedTotalFormatted === choice.originalTotalFormatted
            ? `${this.dataset.bundleTotalLabel}: ${choice.originalTotalFormatted}`
            : `${this.dataset.bundleTotalLabel}: ${choice.originalTotalFormatted} ${this.dataset.bundleDiscountedTotalLabel}: ${choice.discountedTotalFormatted}`;
        label.append(total);
      }

      this._bundleOptions.append(label);
    });
  }

  renderCarousel(images) {
    const carouselImages = Array.isArray(images) ? images : [];
    this._carouselTrack.querySelectorAll('video').forEach((video) => video.pause());
    this._carouselTrack.replaceChildren();
    this._carouselDots.replaceChildren();
    this._carouselIndex = 0;

    if (!carouselImages.length) {
      this._carouselElement.hidden = true;
      return;
    }

    this._carouselElement.hidden = false;
    this._carouselPrevious.hidden = carouselImages.length < 2;
    this._carouselNext.hidden = carouselImages.length < 2;

    carouselImages.forEach((image, index) => {
      const element = document.createElement(image.type === 'video' ? 'video' : 'img');
      if (image.type === 'video') {
        element.muted = true;
        element.loop = true;
        element.playsInline = true;
        element.preload = 'metadata';
        element.setAttribute('aria-label', image.alt || '');
        if (image.poster) element.poster = image.poster;
        (image.sources || []).forEach((source) => {
          const sourceElement = document.createElement('source');
          sourceElement.src = source.url;
          sourceElement.type = source.type;
          element.append(sourceElement);
        });
      } else {
        element.src = image.url;
        element.alt = image.alt || '';
        element.loading = image.loading || 'lazy';
      }
      element.dataset.carouselImage = '';
      this._carouselTrack.append(element);

      const dot = document.createElement('button');
      dot.type = 'button';
      dot.dataset.carouselDot = '';
      dot.textContent = String(index + 1);
      dot.addEventListener('click', () => {
        this.showCarouselImage(index);
      });
      this._carouselDots.append(dot);
    });

    this.showCarouselImage(0);
  }

  showCarouselImage(index) {
    const images = Array.from(this.querySelectorAll('[data-carousel-image]'));
    const dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));

    if (!images.length) {
      return;
    }

    this._carouselIndex = (index + images.length) % images.length;

    images.forEach((image, imageIndex) => {
      image.hidden = imageIndex !== this._carouselIndex;
      if (image instanceof HTMLVideoElement) {
        if (imageIndex === this._carouselIndex) image.play().catch(() => {});
        else image.pause();
      }
    });

    dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-selected', dotIndex === this._carouselIndex);
    });
  }

  preloadVariantImages() {
    const preload = () => {
      const urls = new Set();

      this._variants.forEach((variant) => {
        if (variant.icon) {
          urls.add(variant.icon.url);
        }

        variant.mediaImages.forEach((image) => {
          urls.add(image.url);
        });
      });

      urls.forEach((url) => {
        const image = new Image();
        image.src = url;
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preload);
      return;
    }

    window.setTimeout(preload, 250);
  }
}

if (!customElements.get('product-variants')) {
  customElements.define('product-variants', ProductVariants);
}
